// eyeq — frozen locale set (26 this iteration) + volume tiers.
// Single source of truth for the locale enum. The D1 seed
// (apps/eyeq-api/migrations/0002_seed_locales_plan.sql) and this enum MUST
// stay in lock-step — the round-trip test in packages/contracts/test pins it.
// Canonical key is the underscore form (matches the MobileEyeQ timeline).
import { z } from 'zod';

export const EyeqLocaleSchema = z.enum([
  // Tier A (priority, ≥11K — 67% of the iteration, full-window critical path)
  'ro_RO', 'el_GR', 'cs_CZ', 'hr_HR', 'pl_PL', 'tr_TR', 'ja_JP', 'ko_KR', 'zh_SG',
  // Tier B (mid, 4K–8.4K)
  'zh_HK', 'zh_TW', 'pt_PT', 'it_IT', 'en_SG', 'ar_SA', 'es_ES', 'ar_AE',
  // Tier C (~3.2K)
  'sw_KE', 'vi_VN', 'th_TH', 'hi_IN', 'fil_PH',
  // Tier D (≤1.6K, compact W3–W14 block)
  'pt_BR', 'ar_EG', 'en_KE', 'en_IN',
]);
export type EyeqLocale = z.infer<typeof EyeqLocaleSchema>;

export const EyeqTierSchema = z.enum(['A', 'B', 'C', 'D']);
export type EyeqTier = z.infer<typeof EyeqTierSchema>;

// The full 26-row reference, mirrored from the D1 seed. Used by tests to pin
// the set and by any code that needs country/language/tier without a DB read.
export const EYEQ_LOCALES: ReadonlyArray<{
  locale: EyeqLocale;
  country: string;
  language: string;
  tier: EyeqTier;
  iterTarget: number;
}> = [
  { locale: 'ro_RO', country: 'Romania', language: 'Romanian', tier: 'A', iterTarget: 16670 },
  { locale: 'el_GR', country: 'Greece', language: 'Greek', tier: 'A', iterTarget: 16673 },
  { locale: 'cs_CZ', country: 'Czech Republic', language: 'Czech', tier: 'A', iterTarget: 16673 },
  { locale: 'hr_HR', country: 'Croatia', language: 'Croatian', tier: 'A', iterTarget: 16672 },
  { locale: 'pl_PL', country: 'Poland', language: 'Polish', tier: 'A', iterTarget: 16670 },
  { locale: 'tr_TR', country: 'Turkey', language: 'Turkish', tier: 'A', iterTarget: 16667 },
  { locale: 'ja_JP', country: 'Japan', language: 'Japanese', tier: 'A', iterTarget: 16664 },
  { locale: 'ko_KR', country: 'South Korea', language: 'Korean', tier: 'A', iterTarget: 16663 },
  { locale: 'zh_SG', country: 'Singapore', language: 'Simplified Chinese', tier: 'A', iterTarget: 11665 },
  { locale: 'zh_HK', country: 'Hong Kong', language: 'Traditional Chinese', tier: 'B', iterTarget: 8333 },
  { locale: 'zh_TW', country: 'Taiwan', language: 'Traditional Chinese', tier: 'B', iterTarget: 8335 },
  { locale: 'pt_PT', country: 'Portugal', language: 'Portuguese', tier: 'B', iterTarget: 8331 },
  { locale: 'it_IT', country: 'Italy', language: 'Italian', tier: 'B', iterTarget: 8295 },
  { locale: 'en_SG', country: 'Singapore', language: 'English', tier: 'B', iterTarget: 5005 },
  { locale: 'ar_SA', country: 'Saudi Arabia', language: 'Arabic', tier: 'B', iterTarget: 4167 },
  { locale: 'es_ES', country: 'Spain', language: 'Spanish', tier: 'B', iterTarget: 4166 },
  { locale: 'ar_AE', country: 'UAE', language: 'Arabic', tier: 'B', iterTarget: 4166 },
  { locale: 'sw_KE', country: 'Kenya', language: 'Swahili', tier: 'C', iterTarget: 3203 },
  { locale: 'vi_VN', country: 'Vietnam', language: 'Vietnamese', tier: 'C', iterTarget: 3203 },
  { locale: 'th_TH', country: 'Thailand', language: 'Thai', tier: 'C', iterTarget: 3203 },
  { locale: 'hi_IN', country: 'India', language: 'Hindi', tier: 'C', iterTarget: 3202 },
  { locale: 'fil_PH', country: 'Philippines', language: 'Filipino/Tagalog', tier: 'C', iterTarget: 3202 },
  { locale: 'pt_BR', country: 'Brazil', language: 'Portuguese', tier: 'D', iterTarget: 1597 },
  { locale: 'ar_EG', country: 'Egypt', language: 'Arabic', tier: 'D', iterTarget: 803 },
  { locale: 'en_KE', country: 'Kenya', language: 'English', tier: 'D', iterTarget: 350 },
  { locale: 'en_IN', country: 'India', language: 'English', tier: 'D', iterTarget: 350 },
];

// Frozen iteration total (matches the timeline; guarded by test).
export const EYEQ_ITERATION_TOTAL = 214928;
